import React from 'react';
import { Phone, Mail, MapPin, Clock, MessageCircle, ExternalLink, Flame } from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import { useSettings } from '../hooks/useSettings';

const Contact = () => {
  const { settings, loading } = useSettings();

  const whatsappNumber = settings?.whatsapp_number || '+56 9 0000 0000';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}`;
  const email = settings?.email || 'contacto@sammygas.cl';
  const address = settings?.address || 'Av. Principal 123, Ciudad';
  const businessName = settings?.business_name || 'SammyGas';
  const businessHours = settings?.business_hours || 'Lunes a Viernes: 08:00 - 20:00';

  const contactInfo = [
    {
      icon: Phone,
      title: 'WhatsApp',
      value: whatsappNumber,
      description: 'Respuesta rápida garantizada',
      action: {
        label: 'Enviar mensaje',
        href: whatsappLink,
      },
      color: 'bg-green-50 text-green-600',
    },
    {
      icon: Mail,
      title: 'Correo electrónico',
      value: email,
      description: 'Para consultas generales',
      action: {
        label: 'Enviar email',
        href: `mailto:${email}`,
      },
      color: 'bg-blue-50 text-blue-600',
    },
    {
      icon: MapPin,
      title: 'Dirección',
      value: address,
      description: 'Nuestra ubicación',
      action: {
        label: 'Ver en mapa',
        href: 'https://maps.google.com',
      },
      color: 'bg-orange-50 text-orange-600',
    },
  ];

  // Parse business hours if it's a simple string
  const schedule = businessHours.includes('\n') 
    ? businessHours.split('\n').map(line => {
        const [day, hours] = line.split(':');
        return { day: day?.trim() || '', hours: hours?.trim() || '' };
      })
    : [
        { day: 'Horario', hours: businessHours },
      ];

  return (
    <div className="min-h-screen bg-background pt-20 pb-24 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#E85D04]/10 flex items-center justify-center">
            <MessageCircle size={32} className="text-[#E85D04]" />
          </div>
          <h1 className="text-3xl font-display font-bold text-[#1B263B] mb-2">
            Contáctanos
          </h1>
          <p className="text-[#7A756F]">
            Estamos aquí para ayudarte con tu pedido
          </p>
        </div>

        {/* Main CTA */}
        <Card className="mb-6 bg-gradient-to-r from-[#38B000] to-[#4CD015] text-white border-0">
          <div className="text-center">
            <h2 className="font-display font-bold text-xl mb-2">
              ¿Necesitas hacer un pedido urgente?
            </h2>
            <p className="text-white/90 mb-4">
              Escríbenos por WhatsApp y te atenderemos de inmediato
            </p>
            <a 
              href={whatsappLink}
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                variant="secondary" 
                size="lg" 
                className="gap-2 bg-white text-[#38B000] hover:bg-white/90"
              >
                <MessageCircle size={20} />
                Abrir WhatsApp
              </Button>
            </a>
          </div>
        </Card>

        {/* Contact Methods */}
        <div className="space-y-4 mb-8">
          {contactInfo.map((item) => (
            <Card key={item.title} className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                <item.icon size={24} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-[#1B263B] mb-0.5">
                  {item.title}
                </h3>
                <p className="font-semibold text-[#1B263B] mb-1">
                  {item.value}
                </p>
                <p className="text-sm text-[#7A756F] mb-3">
                  {item.description}
                </p>
                <a
                  href={item.action.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[#E85D04] hover:underline"
                >
                  {item.action.label}
                  <ExternalLink size={14} />
                </a>
              </div>
            </Card>
          ))}
        </div>

        {/* Schedule */}
        <Card className="mb-6">
          <h2 className="font-display font-bold text-lg text-[#1B263B] mb-4 flex items-center gap-2">
            <Clock size={20} className="text-[#E85D04]" />
            Horario de atención
          </h2>
          <div className="space-y-3">
            {schedule.map((item) => (
              <div
                key={item.day}
                className="flex items-center justify-between py-2 border-b border-[#EFEDEA] last:border-0"
              >
                <span className="text-[#7A756F]">{item.day}</span>
                <span className="font-semibold text-[#1B263B]">{item.hours}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Brand Info */}
        <div className="text-center p-6 bg-[#1B263B] rounded-2xl">
          <div className="w-16 h-16 mx-auto mb-4 rounded-xl bg-[#E85D04] flex items-center justify-center">
            <Flame size={32} className="text-white" />
          </div>
          <h3 className="font-display font-bold text-xl text-white mb-2">
            {businessName}
          </h3>
          <p className="text-white/70 text-sm mb-4">
            Tu gas a domicilio, rápido y seguro.
            <br />
            Confianza y calidad en cada entrega.
          </p>
          <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
            <span>© 2026 {businessName}</span>
            <span>•</span>
            <span>Todos los derechos reservados</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
